/// <amd-module name="VSSUI/Utilities/AggregateItemProvider" />
import { IObservableArrayEventArgs, IReadonlyObservableArray, Observable, ObservableArrayAction, ObservableLike } from "VSS/Core/Observable";
import { IItemProvider } from "VSSUI/Utilities/Provider";

interface IAggregateItemProviderEntry<T> {
    items: T[];
    provider?: IReadonlyObservableArray<T> | IItemProvider<T>;
    subscriber?: (args: IObservableArrayEventArgs<T>) => void;
}

/**
 * An AggregateItemProvider takes a set of arrays, ObservableArrays, and ItemProviders and returns listItems from them as if they were a single
 * ItemProvider.
 */
export class AggregateItemProvider<T> extends Observable<IObservableArrayEventArgs<T>, ObservableArrayAction> implements IItemProvider<T> {
    public get length(): number {
        return this.items.length;
    }

    public get value(): T[] {
        return this.items;
    }

    public get collections(): Array<IAggregateItemProviderEntry<T>> {
        return this.internalCollections;
    }

    private internalCollections: Array<IAggregateItemProviderEntry<T>> = [];
    private items: T[] = [];

    /**
     * Adds an additional collection of items to the end of the array
     *
     * @param collection Array of items or an observable array of items
     */
    public push(collection: T[] | IReadonlyObservableArray<T> | IItemProvider<T>): void {
        let collectionEntry: IAggregateItemProviderEntry<T> | undefined;

        if (ObservableLike.isObservable(collection)) {
            const provider = collection as IReadonlyObservableArray<T>;
            const subscriber = this.getSubscriber(this.internalCollections.length);
            collectionEntry = { items: provider.value, provider, subscriber };

            if (this.subscriberCount) {
                ObservableLike.subscribe(collectionEntry.provider, subscriber);
            }
        } else if ((collection as IItemProvider<T>).value) {
            collectionEntry = { items: (collection as IItemProvider<T>).value, provider: collection as IItemProvider<T> };
        } else if ((collection as T[]).length) {
            collectionEntry = { items: collection as T[] };
        }

        if (collectionEntry) {
            this.internalCollections.push(collectionEntry);

            if (collectionEntry.items.length) {
                this.items.push(...collectionEntry.items);
                if (this.subscriberCount) {
                    this.notify({ addedItems: collectionEntry.items, index: this.items.length - collectionEntry.items.length }, "push");
                }
            }
        }
    }

    public getItem(index: number): T | undefined {
        const count = 0;
        let collectionIndex = 0;
        while (
            collectionIndex < this.internalCollections.length &&
            count +
                (this.internalCollections[collectionIndex].provider
                    ? this.internalCollections[collectionIndex].provider!.length
                    : this.internalCollections[collectionIndex].items.length) <
                index
        ) {
            collectionIndex++;
        }
        if (collectionIndex < this.internalCollections.length) {
            if (
                this.internalCollections[collectionIndex].provider &&
                (this.internalCollections[collectionIndex].provider as IItemProvider<T>).getItem
            ) {
                return (this.internalCollections[collectionIndex].provider as IItemProvider<T>).getItem!(index);
            } else {
                return this.internalCollections[collectionIndex].items[index] as T;
            }
        }
    }

    public subscribe(observer: (value: IObservableArrayEventArgs<T>, action: ObservableArrayAction) => void, action?: ObservableArrayAction): (value: IObservableArrayEventArgs<T>, action: ObservableArrayAction) => void {
        const subscription = super.subscribe(observer, action);
        if (this.subscriberCount === 1) {
            for (const collection of this.internalCollections) {
                if (collection.subscriber && collection.provider && collection.provider.subscribe) {
                    collection.provider.subscribe(collection.subscriber);
                }
            }
            // Collections may have changed between the time they were pushed and subscribed to.
            // Update the aggregate provider's items to match the current state of its collections.
            const currentItems: T[] = [];
            for (const collection of this.internalCollections) {
                currentItems.push(...collection.items);
            }
            this.items.splice(0, this.items.length, ...currentItems);
        }

        return subscription;
    }

    public unsubscribe(observer: (value: IObservableArrayEventArgs<T>, action: ObservableArrayAction) => void, action?: ObservableArrayAction): void {
        super.unsubscribe(observer, action);
        if (this.subscriberCount === 0) {
            for (const collection of this.internalCollections) {
                if (collection.subscriber && collection.provider && collection.provider.unsubscribe) {
                    collection.provider!.unsubscribe(collection.subscriber);
                }
            }
        }
    }

    private getSubscriber(collectionIndex: number): (args: IObservableArrayEventArgs<T>) => void {
        return (args: IObservableArrayEventArgs<T>) => {
            // Find the index in our aggregate array
            let index = args.index;
            for (let i = 0; i < collectionIndex; i++) {
                index += this.internalCollections[i].items.length;
            }

            if (args.changedItems) {
                // Handle change event
                this.items.splice(index, args.changedItems.length, ...args.changedItems);
                this.notify({ changedItems: args.changedItems, index }, "change");
            } else {
                // Handle splice, push, pop events
                const removedItems = args.removedItems || [];
                const addedItems = args.addedItems || [];
                this.items.splice(index, removedItems.length, ...addedItems);
                this.notify({ removedItems, addedItems, index }, "splice");
            }
        };
    }
}
